<template>
    <BaseModal :title="user ? $t('titleEdit') : $t('title')" v-on="$listeners">
        <BaseCard padded class="l-stack l-gap-2">
            <label v-if="!user">
                <p v-if="errors.username" class="form-error">
                    {{ errors.username }}
                </p>

                <BaseInput v-model="username" :placeholder="$t('username')" />
            </label>

            <label>
                <p v-if="errors.first_name" class="form-error">
                    {{ errors.first_name }}
                </p>

                <BaseInput v-model="firstName" :placeholder="$t('firstName')" />
            </label>

            <label>
                <p v-if="errors.last_name" class="form-error">
                    {{ errors.last_name }}
                </p>

                <BaseInput v-model="lastName" :placeholder="$t('lastName')" />
            </label>

            <label>
                <p
                    v-if="
                        ($v.email.$anyError && $v.email.$dirty) || errors.email
                    "
                    class="form-error"
                >
                    {{ $t('emailInvalid') }}
                </p>

                <BaseInput
                    v-model="$v.email.$model"
                    :placeholder="$t('email')"
                />
            </label>

            <label v-if="!user">
                <p v-if="errors.password" class="form-error">
                    {{ errors.password }}
                </p>

                <BaseInput
                    v-model="password"
                    type="password"
                    :placeholder="$t('password')"
                />
            </label>

            <BaseMultiselect
                v-if="!user"
                v-model="language"
                :options="languages"
                label="label"
                track-by="value"
                :placeholder="$t('language')"
            />

            <BaseMultiselect
                v-model="role"
                :options="roleOptions"
                label="name"
                track-by="id"
                :placeholder="$t('role')"
            />
        </BaseCard>

        <template #footer>
            <div class="l-inline l-justify-end">
                <BaseButton
                    size="small"
                    :disabled="isDisabled"
                    :is-loading="isLoading"
                    @click="handleSubmit"
                >
                    {{ user ? $t('submitEdit') : $t('submit') }}
                </BaseButton>
            </div>
        </template>
    </BaseModal>
</template>

<script>
import { email } from 'vuelidate/lib/validators'

import { httpHelper } from '@/utils'
import BaseButton from './BaseButton'
import BaseCard from './BaseCard'
import BaseInput from './BaseInput'
import BaseModal from './BaseModal'
import BaseMultiselect from './BaseMultiselect'

export default {
    name: 'UserEditModal',
    components: {
        BaseButton,
        BaseCard,
        BaseInput,
        BaseModal,
        BaseMultiselect,
    },
    props: {
        onSubmit: {
            type: Function,
            default: null,
        },
        user: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            email: '',
            errors: {},
            firstName: '',
            isLoading: false,
            lastName: '',
            language: null,
            languages: [
                { label: this.$t('shared.language.de'), value: 'de' },
                { label: this.$t('shared.language.en'), value: 'en' },
                { label: this.$t('shared.language.fr'), value: 'fr' },
                { label: this.$t('shared.language.it'), value: 'it' },
            ],
            password: '',
            role: null,
            roleOptions: [],
            username: '',
        }
    },
    computed: {
        isDisabled() {
            const isMissingCommonFields =
                !this.firstName || !this.lastName || !this.role
            const isMissingCreateFields =
                !this.password || !this.username || !this.language

            return (
                this.$v.$invalid ||
                isMissingCommonFields ||
                (!this.user && isMissingCreateFields)
            )
        },
    },
    watch: {
        email() {
            this.errors.email = null
        },
        firstName() {
            this.errors.first_name = null
        },
        lastName() {
            this.errors.last_name = null
        },
        language() {
            this.errors.language = null
        },
        password() {
            this.errors.password = null
        },
        user: {
            immediate: true,
            handler() {
                this.email = this.user?.email
                this.firstName = this.user?.first_name
                this.lastName = this.user?.last_name
                this.role = this.roleOptions.find(
                    ({ id }) => id === this.user?.role
                )
            },
        },
        username() {
            this.errors.username = null
        },
    },
    validations() {
        return {
            email: {
                email,
            },
        }
    },
    async mounted() {
        try {
            this.isLoading = true
            const { data } = await httpHelper.getRecursively('/roles/')
            this.roleOptions = data.results

            if (this.user) {
                this.role = this.roleOptions.find(
                    ({ id }) => id === this.user.role
                )
            } else {
                this.language =
                    this.languages.find(
                        ({ value }) => value === this.$i18n.locale
                    ) || this.languages[0]
            }
        } finally {
            this.isLoading = false
        }
    },
    methods: {
        async handleSubmit() {
            try {
                this.isLoading = true
                const data = {
                    email: this.email,
                    first_name: this.firstName,
                    last_name: this.lastName,
                    ...(this.role?.id !== undefined &&
                        this.role?.id !== this.user?.role && {
                            role: this.role.id,
                        }),
                    account: this.user?.account,
                }

                if (this.user) {
                    await httpHelper.patch(`/users/${this.user.id}/`, data)
                } else {
                    await httpHelper.post('/users/', {
                        ...data,
                        language: this.language.value,
                        password: this.password,
                        platform: 'ax-track',
                        username: this.username,
                    })
                }
                this.onSubmit?.()
                this.$emit('close')
            } catch (e) {
                this.errors = Object.entries(e.response?.data || {}).reduce(
                    (acc, [key, value]) => ({
                        ...acc,
                        [key]: Array.isArray(value) ? value[0] : value,
                    }),
                    {}
                )
            } finally {
                this.isLoading = false
            }
        },
    },
}
</script>

<i18n>
{
    "en": {
        "actions": "Actions",
        "email": "Email",
        "emailInvalid": "Invalid E-Mail",
        "firstName": "First Name",
        "language": "Language",
        "lastName": "Last Name",
        "password": "Password",
        "role": "Role",
        "submit": "Create",
        "submitEdit": "Apply",
        "title": "Create user",
        "titleEdit": "Edit user",
        "username": "Username"
    },
    "de": {
        "actions": "Aktionen",
        "email": "Email",
        "emailInvalid": "Ungültige E-Mail",
        "firstName": "Vorname",
        "language": "Sprache",
        "lastName": "Nachname",
        "password": "Passwort",
        "role": "Rolle",
        "submit": "Erstellen",
        "submitEdit": "Anwenden",
        "title": "Neuer Benutzer",
        "titleEdit": "Benutzer bearbeiten",
        "username": "Benutzername"
    },
    "fr": {
        "actions": "Actions",
        "email": "E-mail",
        "emailInvalid": "E-mail invalide",
        "firstName": "Prénom",
        "lastName": "Nom",
        "language": "Langue",
        "password": "Mot de passe",
        "role": "Rôle",
        "submit": "Créer",
        "submitEdit": "Appliquer",
        "title": "Nouvel utilisateur",
        "titleEdit": "Modifier l'utilisateur",
        "username": "Nom d'utilisateur"
    },
    "it": {
        "email": "E-mail",
        "emailInvalid": "E-Mail non valido",
        "firstName": "Prenome",
        "lastName": "Cognome",
        "language": "Lingua",
        "password": "Password",
        "role": "Ruolo",
        "submit": "Creare",
        "submitEdit": "Applicare",
        "title": "Nuovo utente",
        "titleEdit": "Modifica utente",
        "username": "Nome utente"
    }
}
</i18n>
